<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useOnline } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { Globals } from '@/modules/Globals';

  /**
   * «Foto» of a dish (plan §4.5.1): a 96 px slot with the photo, «Agregar
   * foto» / «Quitar foto»; tapping the photo replaces it. The picked file is
   * handed over (`pick`): the menu flow resizes, uploads and saves it, so
   * the upload goes on when this editor closes (`uploading` shows it).
   */
  const props = withDefaults(
    defineProps<{
      /** `temp/…` (an upload) or a final key (a copied dish); null = no photo. */
      value?: string | null;
      /** A new photo of this dish is on its way. */
      uploading?: boolean;
      disabled?: boolean;
      /**
       * «Quitar» under the tile, and no «Agregar foto» button (the empty
       * tile adds one): for narrow forms such as the Menú page's editor.
       */
      compact?: boolean;
    }>(),
    { value: null, uploading: false, disabled: false, compact: false }
  );
  const emit = defineEmits<{
    (e: 'pick', file: File): void;
    /** «Quitar foto». */
    (e: 'remove'): void;
  }>();
  const online = useOnline();

  const fileInput = ref<HTMLInputElement | null>(null);
  /** The image failed to load: the empty slot stands in. */
  const imageFailed = ref(false);

  const imageUrl = computed(() =>
    props.value && !imageFailed.value
      ? `${Globals.uploadsPath}/${props.value}`
      : null
  );
  /** Picking needs the network (the upload starts right away). */
  const pickDisabled = computed(
    () => props.disabled || props.uploading || !online.value
  );

  function choose() {
    if (!pickDisabled.value) fileInput.value?.click();
  }

  function onFileChange(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const file = input.files?.[0] ?? null;
    // Picking the same file again must fire `change` again.
    input.value = '';
    if (file) emit('pick', file);
  }

  function remove() {
    if (!props.disabled && !props.uploading) emit('remove');
  }

  watch(
    () => props.value,
    () => {
      imageFailed.value = false;
    }
  );
</script>

<template>
  <div class="v2-menu-photo-slot" :class="{ 'is-compact': compact }">
    <button
      type="button"
      class="slot-tile"
      :class="{ 'is-busy': uploading, 'has-image': !!imageUrl }"
      :aria-label="$t('menuImport.editor.addPhoto')"
      :disabled="pickDisabled"
      @click="choose"
    >
      <span v-if="uploading" class="tile-spinner" />
      <img
        v-else-if="imageUrl"
        class="tile-image"
        :src="imageUrl"
        alt=""
        @error="imageFailed = true"
      />
      <PosUiIcon v-else name="camera-plus-outline" :size="28" />
    </button>
    <div v-if="value || !compact" class="slot-actions">
      <PosUiButton
        v-if="!value"
        color="neutral"
        variant="outline"
        icon="camera-outline"
        :disabled="pickDisabled"
        @click="choose"
        >{{ $t('menuImport.editor.addPhoto') }}</PosUiButton
      >
      <button
        v-else
        type="button"
        class="slot-remove"
        :disabled="disabled || uploading"
        @click="remove"
      >
        {{ $t('menuImport.editor.removePhoto') }}
      </button>
    </div>
    <input
      ref="fileInput"
      class="slot-input"
      type="file"
      accept="image/*"
      tabindex="-1"
      aria-hidden="true"
      @change="onFileChange"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-photo-slot {
    @apply tw-flex tw-items-center;

    .slot-tile {
      @apply tw-flex tw-h-24 tw-w-24 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border tw-border-dashed tw-border-neutral-300 tw-bg-neutral-50 tw-p-0 tw-text-neutral-400 tw-transition-colors hover:tw-border-neutral-400 hover:tw-text-neutral-600;
      &.has-image {
        @apply tw-border-solid tw-border-neutral-200;
      }
      &:disabled {
        @apply tw-cursor-default hover:tw-border-neutral-300 hover:tw-text-neutral-400;
      }
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .tile-image {
      @apply tw-h-full tw-w-full tw-object-cover;
    }
    .tile-spinner {
      @apply tw-h-6 tw-w-6 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-400 tw-border-t-transparent;
    }
    .slot-actions {
      // Margin, not flex gap: old POS hardware (Chrome < 84). It holds one
      // control at a time.
      @apply tw-ml-4 tw-flex tw-min-w-0 tw-flex-col tw-items-start;
    }
    .slot-remove {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-px-0 tw-text-sm tw-font-medium tw-text-neutral-700 tw-underline tw-underline-offset-4;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .slot-input {
      @apply tw-hidden;
    }
    &.is-compact {
      @apply tw-flex-col tw-items-center;
      .slot-actions {
        @apply tw-ml-0 tw-mt-1 tw-items-center;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-photo-slot .tile-spinner {
      animation: none;
    }
  }
</style>
